import { Dispatch } from "redux";
import { createApiClient, apiClient, getLocalTimestamp } from "../../api/apiClient";
import { setObservations } from "../../store/slices/observationSlice";
import {
  ObservationAPIData,
  ObservationRowData,
} from "../../types/observation";
import { formatDate } from "../../utils/dateFormatter";
import { handleObservationErrorWithFriendlyMessage, OBSERVATION_API_NAMES } from "../../store/utils/observationErrorHandler";

interface FetchObservationsDataProps {
  selectedLocation: { id: string; name?: string; displayText?: string } | null;
  setIsLoading: (isLoading: boolean) => void;
  setApiError: (error: string | null) => void;
  setObservationsData: (data: ObservationRowData[]) => void;
  setFullData?: (data: ObservationAPIData[]) => void; // Optional setter for raw API data
  dispatch: Dispatch;
  selectedObservationViewFilters?: {
    observationType?: string;
    shiftNumber?: string;
    roleObserved?: string;
    status?: string;
    createdBy?: string;
    modifiedBy?: string;
    period?: string;
    week?: string;
    [key: string]: string | undefined;
  };
  applyFilters?: boolean;
}

/**
 * Fetches observation data from the API
 * Can optionally apply filters from selectedObservationViewFilters
 * @param props Object containing required parameters and optional filters
 * @returns Promise<void>
 */
export const fetchObservationsData = async ({
  selectedLocation,
  setIsLoading,
  setApiError,
  setObservationsData,
  setFullData,
  dispatch,
  selectedObservationViewFilters = {},
  applyFilters = false,
}: FetchObservationsDataProps): Promise<void> => {
  if (!selectedLocation) {
    setApiError("No location selected");
    return;
  }

  setIsLoading(true);
  setApiError(null);

  try {
    // Use either name or displayText property depending on what's available
    const locationName =
      selectedLocation.name || selectedLocation.displayText || "";
    const location = encodeURIComponent(locationName);
    let endpoint = `/observations?location=${location}&clientTimestamp=${getLocalTimestamp()}`;
    // Only add filter parameters if applyFilters is true and we have filters to apply
    if (
      applyFilters &&
      Object.keys(selectedObservationViewFilters).length > 0
    ) {
      const queryParams: string[] = [];
      // Map the Redux store field names to API query parameters
      const fieldToQueryParamMap: { [key: string]: string } = {
        observationType: "observationType",
        shiftNumber: "shiftNumber",
        roleObserved: "roleObserved",
        status: "status",
        createdBy: "createdBy",
        modifiedBy: "modifiedBy",
        period: "period",
        week: "week",
      };

      // Add filters to query parameters from Redux store
      Object.entries(selectedObservationViewFilters).forEach(([key, value]) => {
        if (value && typeof value === "string") {
          // Use the mapped param name if available, otherwise use the key as is
          const queryParamName = fieldToQueryParamMap[key] || key;
          queryParams.push(`${queryParamName}=${encodeURIComponent(value)}`);
        }
      });
      if (queryParams.length > 0) {
        endpoint += `&${queryParams.join("&")}`;
      }
    }

    // Create API client instance
    const api = createApiClient();

    // Use apiClient instead of fetch
    const response: { observations: ObservationAPIData[]; metrics?: any } = await apiClient(api, endpoint, "GET");
    
    // Validate response structure
    if (!response || typeof response !== 'object') {
      const noDataError = new Error("Invalid response format from API");
      
      handleObservationErrorWithFriendlyMessage(
        OBSERVATION_API_NAMES.FETCH_OBSERVATIONS,
        noDataError,
        true,
        {
          location: selectedLocation?.name || selectedLocation?.displayText,
          filters: applyFilters ? selectedObservationViewFilters : undefined,
          reason: "Invalid response structure"
        }
      );
      
      setApiError("Unable to load observations. Invalid response format.");
      setObservationsData([]);
      return;
    }
    
    // Extract observations from the response
    const data: ObservationAPIData[] = response.observations || [];

    // Store the full data if the setter is provided
    if (typeof setFullData === "function") {
      setFullData(data);
    }

    // dispatch to Observation slice
    dispatch(setObservations(data)); // Map API data to our table format
    const formattedData = data.map((item: ObservationAPIData) => ({
      id: item.ID,
      type: item.New_Hire_Annual_or_Routine,
      employee: `${item.EE_First_Name} ${item.EE_Last_Name}`,
      // Split shift and role into separate fields for better filtering
      shift: item.Shift_Number || "N/A",
      role: item.Role_Observed || "N/A",
      // Keep details for backward compatibility
      details: `${item.Shift_Number} - ${item.Role_Observed}`,
      created: item.Created ? formatDate(item.Created) : "N/A",
      by: item.Created_By,
      modified: item.Modified ? formatDate(item.Modified) : "N/A",
      modifiedBy: item.Modified_By || "N/A",
      period: item.Period
        ? `P${item.Period}${item.Week ? `W${item.Week}` : ""}`
        : "N/A",
      action: item.Status,
      // Preserve all original data for detailed view
      rawData: item,
    }));

    setObservationsData(formattedData);
  } catch (err) {
    console.error("Error fetching observations:", err);
    
    // Use centralized error handler with overlay
    handleObservationErrorWithFriendlyMessage(
      OBSERVATION_API_NAMES.FETCH_OBSERVATIONS,
      err,
      true, // Show error modal overlay
      {
        location: selectedLocation?.name || selectedLocation?.displayText,
        filters: applyFilters ? selectedObservationViewFilters : undefined,
      }
    );
    
    setApiError(
      err instanceof Error ? err.message : "Failed to fetch observations data"
    );
    setObservationsData([]); // Clear data on error
  } finally {
    setIsLoading(false);
  }
};
